/**
 * Frontend Pricing Logic — Product and order-total conditional logic rules.
 *
 * Handles rules whose `fieldId` uses the `gpacl_pricing_` prefix:
 *
 *   - `gpacl_pricing_price_{id}`    → Product field unit price (base + options)
 *   - `gpacl_pricing_quantity_{id}` → Product field quantity
 *   - `gpacl_pricing_subtotal`      → Order subtotal
 *   - `gpacl_pricing_tax`           → Order tax
 *
 * These values are derived, so — unlike user rules — they cannot be resolved
 * server-side and localized. They are recomputed on every evaluation from
 * whatever the shopper has currently selected.
 *
 * Re-evaluation is driven by the dependency-tracking sentinel rules that
 * Pricing_Logic::get_dependency_field_ids() adds on the PHP side, which point
 * GF at the real pricing fields behind each synthetic fieldId.
 *
 * @package
 */

import { registerModule } from './module-registry';
import { matchesConditionalOperation } from './conditional-operation';
import {
	PRICING_PREFIX,
	PRICING_PRICE_PREFIX,
	PRICING_QUANTITY_PREFIX,
	PRICING_SUBTOTAL_ID,
	PRICING_TAX_ID,
	pricingProductFieldId,
} from '../shared/helpers/pricing-rules';

/* eslint-disable @typescript-eslint/no-explicit-any */

/** One product as GF 3.0 stores it in `gform.state`. */
interface ProductState {
	id: string | number;
	price: number;
	quantity: number;
	options?: Array<{ price: number }>;
}

/** The `products` slice of GF 3.0's form state. */
interface ProductsState {
	products?: ProductState[];
	shipping?: { price: number };
}

/**
 * Read GF 3.0's products state for a form.
 *
 * Returns null on GF 2.x, where the state module does not exist and we fall
 * back to the DOM-reading helpers instead.
 *
 * @param formId The form ID.
 */
function getProductsState(formId: number): ProductsState | null {
	const state = (window as any).gform?.state;

	if (!state || typeof state.get !== 'function') {
		return null;
	}

	try {
		return (state.get(formId, 'products') as ProductsState) ?? null;
	} catch (e) {
		return null;
	}
}

/**
 * Find a single product in GF's products state.
 *
 * @param formId    The form ID.
 * @param productId The Product field ID.
 */
function findProduct(formId: number, productId: number): ProductState | null {
	const state = getProductsState(formId);

	if (!state || !Array.isArray(state.products)) {
		return null;
	}

	return (
		state.products.find(
			(product) => parseFloat(String(product.id)) === productId
		) ?? null
	);
}

/**
 * Get a product's unit price, including the price of any selected options.
 *
 * Matches Pricing_Logic::get_product_price() on the PHP side so both sides of
 * the submission agree.
 *
 * @param product The product from GF's state.
 */
function getUnitPrice(product: ProductState): number {
	return (product.options ?? []).reduce(
		(price, option) => price + (Number(option.price) || 0),
		Number(product.price) || 0
	);
}

/**
 * Get a Product field's unit price.
 *
 * @param formId    The form ID.
 * @param productId The Product field ID.
 */
function getProductPrice(formId: number, productId: number): number {
	const product = findProduct(formId, productId);

	if (product) {
		return getUnitPrice(product);
	}

	if (getProductsState(formId)) {
		// State exists but this product isn't in it — it is unselected or
		// hidden, which is worth exactly nothing.
		return 0;
	}

	// GF 2.x fallback.
	if (typeof (window as any).gformGetBasePrice === 'function') {
		return (
			Number((window as any).gformGetBasePrice(formId, productId)) || 0
		);
	}

	return 0;
}

/**
 * Get a Product field's quantity.
 *
 * @param formId    The form ID.
 * @param productId The Product field ID.
 */
function getProductQuantity(formId: number, productId: number): number {
	const product = findProduct(formId, productId);

	if (product) {
		return Number(product.quantity) || 0;
	}

	if (getProductsState(formId)) {
		return 0;
	}

	// GF 2.x fallback.
	if (typeof (window as any).gformGetProductQuantity === 'function') {
		return (
			Number(
				(window as any).gformGetProductQuantity(formId, productId)
			) || 0
		);
	}

	return 0;
}

/**
 * Sum the visible GP eCommerce Fields inputs of a given type.
 *
 * GPECF keeps each Subtotal / Tax field's computed amount in a hidden input
 * (`.ginput_subtotal_input`, `.ginput_tax_input`) which it rewrites on every
 * price change. Reading those inputs — rather than recalculating — guarantees
 * the rule sees exactly the figure the shopper does.
 *
 * Returns null when the form has no field of that type, so the caller can fall
 * back to computing the value itself.
 *
 * @param formId The form ID.
 * @param type   The GPECF field type ('subtotal' or 'tax').
 */
function sumGPECFFields(formId: number, type: string): number | null {
	const $ = (window as any).jQuery;

	if (!$) {
		return null;
	}

	const $inputs = $(
		`#gform_wrapper_${formId} .ginput_${type}_input`
	) as JQuery<HTMLInputElement>;

	if (!$inputs.length) {
		return null;
	}

	let total = 0;

	$inputs.each(function (this: HTMLInputElement) {
		// A field hidden by conditional logic contributes nothing, exactly as
		// GPECF's own gform_product_total handlers treat it.
		if (
			typeof (window as any).gformIsHidden === 'function' &&
			(window as any).gformIsHidden($(this))
		) {
			return;
		}

		total += parseFloat(this.value) || 0;
	});

	return total;
}

/**
 * Get the order subtotal.
 *
 * Prefers the value shown by a GPECF Subtotal field. When the form has none,
 * falls back to summing the line totals of every product in GF's state, which
 * mirrors Pricing_Logic::get_subtotal()'s own fallback.
 *
 * @param formId The form ID.
 */
function getSubtotal(formId: number): number {
	const fromFields = sumGPECFFields(formId, 'subtotal');

	if (fromFields !== null) {
		return fromFields;
	}

	const state = getProductsState(formId);

	if (!state || !Array.isArray(state.products)) {
		return 0;
	}

	return Math.max(
		0,
		state.products.reduce(
			(subtotal, product) =>
				subtotal +
				getUnitPrice(product) * (Number(product.quantity) || 0),
			0
		)
	);
}

/**
 * Get the total tax on the order.
 *
 * Tax is a GPECF concept with no GF-native equivalent, so a form without Tax
 * fields simply has no tax.
 *
 * @param formId The form ID.
 */
function getTax(formId: number): number {
	return sumGPECFFields(formId, 'tax') ?? 0;
}

/**
 * Resolve a pricing fieldId to its current value.
 *
 * @param fieldId The pricing fieldId.
 * @param formId  The form ID.
 */
function getPricingValue(fieldId: string, formId: number): number {
	if (fieldId === PRICING_SUBTOTAL_ID) {
		return getSubtotal(formId);
	}

	if (fieldId === PRICING_TAX_ID) {
		return getTax(formId);
	}

	const productId = pricingProductFieldId(fieldId);

	if (!productId) {
		return 0;
	}

	if (fieldId.indexOf(PRICING_QUANTITY_PREFIX) === 0) {
		return getProductQuantity(formId, productId);
	}

	if (fieldId.indexOf(PRICING_PRICE_PREFIX) === 0) {
		return getProductPrice(formId, productId);
	}

	return 0;
}

/**
 * Normalize a value to a plain decimal string for comparison.
 *
 * Mirrors Pricing_Logic::to_comparable_string(). Trailing zeros are trimmed so
 * "10.00" and "10" match under `is`, which GF compares as strings, and so both
 * sides survive `gf_matches_operation`'s float conversion for `>` and `<`.
 *
 * @param value The value to normalize. Currency formatting is stripped.
 */
function toComparableString(value: string | number): string {
	let raw = String(value).trim();

	if (raw === '') {
		return '';
	}

	const cleanNumber = (window as any).gform?.Currency?.cleanNumber;

	if (typeof cleanNumber === 'function') {
		const cleaned = cleanNumber(raw, '', '', '.');
		if (cleaned !== false && cleaned !== '' && cleaned !== null) {
			raw = String(cleaned);
		}
	}

	const num = parseFloat(raw);

	if (isNaN(num)) {
		return String(value).trim();
	}

	return String(parseFloat(num.toFixed(4)));
}

// Register with the module registry on import. Must come after advanced-logic
// so its sentinel interception still runs first.
registerModule({
	slug: 'pricing-logic',
	handles: (rule) => String(rule.fieldId).indexOf(PRICING_PREFIX) === 0,
	evaluate: (isMatch, formId, rule) => {
		const source = getPricingValue(String(rule.fieldId), formId);

		return matchesConditionalOperation(rule.operator, (operator) =>
			gf_matches_operation(
				toComparableString(source),
				toComparableString(rule.value),
				operator
			)
		);
	},
});
